import React, { useMemo, useState } from 'react';

import { Area, AreaChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import ReportChartTooltip from '~/components/Common/ReportChartTooltip';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';

import type { LensFilterValues } from '~/types/leLens';

import AttributeSelect from './AttributeSelect';
import CascadingAttributeSelect from './CascadingAttributeSelect';
import SupplementalViews from './SupplementalViews';
import { config } from './config';
import { type MarketingPresentation, salesViewDefaults } from './liveDataAdapter';
import { attributeChoices, mediaSplits, viewAttributes } from './marketingControls';
import './performanceMarketingReport.css';
import {
  type Filters,
  type Row,
  attributionGroupOrder,
  buildSeries,
  buildTable,
  cadenceOptions,
  coreTableMetrics,
  defaults,
  dimensions,
  format,
  group,
  label,
  metricOptions,
  metrics,
  productRows,
  seriesColour,
  sum,
  tableMetrics,
  validate,
  value,
} from './presentationModel';
import { marketingCSV, marketingCsvFilename } from './reportCsv';
import { weekdays } from './workflows';

const SOURCE = 'perf_mkt_reporting_roas';
const views = [
  ['Performance', 'Performance Marketing'],
  ['Attribution', 'Comparison across attribution models'],
  ['PoP', 'PoP Reporting'],
  ['Sales', 'Sales'],
  ['Monthly', 'Monthly Performance Comparison'],
  ['Product mix', 'Product mix'],
] as const;
const originalMetrics = metricOptions;
function SelectControl({
  title,
  value: chosen,
  options,
  onChange,
}: {
  title: string;
  value: string;
  options: Array<string>;
  onChange: (v: string) => void;
}) {
  return (
    <label className="pm-control">
      <span>{title}</span>
      <select aria-label={title} value={chosen} onChange={(e) => onChange(e.target.value)}>
        {options.map((v) => (
          <option key={v} value={v}>
            {dimensions[v] ?? v}
          </option>
        ))}
      </select>
    </label>
  );
}
function display(v: number | null, m: string, compact = false) {
  return format(v, m, compact).replace(/^A\$/, '');
}
function Trend({
  title,
  data,
  series,
  metric,
  theme,
  highlight,
  onHighlight,
  relative = false,
  from,
  to,
  cadence,
}: {
  title: string;
  data: ReturnType<typeof buildSeries>['points'];
  series: Array<string>;
  metric: string;
  theme: string;
  highlight: string;
  onHighlight: (name: string) => void;
  relative?: boolean;
  from: string;
  to: string;
  cadence: string;
}) {
  const chartData = data.map((p) => ({
    date: p.date,
    ...Object.fromEntries(series.map((name, i) => ['s' + i, p.values[name]])),
  }));
  const axisColour = theme === 'dark' ? '#C4D8D4' : '#496167';
  const shareMin = Math.min(
    0,
    ...data.map((p) => Object.values(p.values).reduce<number>((a, v) => a + (v !== null && v < 0 ? v : 0), 0)),
  );
  const shareMax = Math.max(
    1,
    ...data.map((p) => Object.values(p.values).reduce<number>((a, v) => a + (v !== null && v > 0 ? v : 0), 0)),
  );
  const colour = (name: string, i: number) => (highlight === name ? '#4EFEB9' : seriesColour(name, i, theme));
  return (
    <section className="pm-plot">
      <h2>{title}</h2>
      <div className="pm-axis-caption">
        {relative
          ? '% of the selected metric within each date'
          : metric + (metrics[metric]?.format === 'money' ? ' (A$)' : '')}
      </div>
      <div className="pm-chart">
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <AreaChart data={chartData} margin={{ top: 12, right: 14, bottom: 32, left: 8 }}>
            <CartesianGrid stroke={theme === 'dark' ? '#294246' : '#E3E7E2'} vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fill: axisColour, fontSize: 11 }}
              tickFormatter={(v) => v.slice(5)}
              minTickGap={34}
              label={{
                value: cadence === 'Weekly' ? 'Week commencing (Monday)' : 'Date',
                position: 'bottom',
                offset: 14,
                fill: axisColour,
                fontSize: 11,
              }}
            />
            <YAxis
              width={62}
              tick={{ fill: axisColour, fontSize: 11 }}
              tickFormatter={(v) => (relative ? `${Math.round(v * 100)}%` : display(v, metric, true))}
              domain={relative ? [shareMin, shareMax] : ['auto', 'auto']}
              ticks={relative ? [0, 0.25, 0.5, 0.75, 1] : undefined}
              allowDataOverflow={relative}
            />
            <ReportChartTooltip
              content={({ active, label: date, payload }) =>
                active && payload?.length ? (
                  <div className="pm-tooltip">
                    <b>{String(date)}</b>
                    {payload.slice(0, 16).map((entry, i) => (
                      <div key={String(entry.dataKey)}>
                        <i style={{ background: entry.color }} />
                        {series[Number(String(entry.dataKey).slice(1))]}:{' '}
                        <b>
                          {format(typeof entry.value === 'number' ? entry.value : null, relative ? 'percent' : metric)}
                        </b>
                      </div>
                    ))}
                  </div>
                ) : null
              }
            />
            {series.map((name, i) => (
              <Area
                key={name}
                stackId="metric"
                fill={colour(name, i)}
                fillOpacity={highlight && highlight !== name ? 0.12 : 0.78}
                dataKey={'s' + i}
                name={name}
                type="linear"
                stroke={colour(name, i)}
                strokeWidth={highlight === name ? 3 : 1.8}
                strokeDasharray={i >= 8 ? (i % 2 ? '5 3' : '2 2') : undefined}
                opacity={highlight && highlight !== name ? 0.16 : 1}
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
                onClick={() => onHighlight(name)}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <p className="pm-source">
        {SOURCE} · {from} to {to} ·{' '}
        {relative ? 'Share of the selected metric in each date.' : 'Click a series or legend to highlight it.'}
      </p>
    </section>
  );
}

export default function PerformanceMarketingReport({
  model,
  filters: f,
  onChange,
  options,
  totals,
  choiceFilters,
  choiceAvailability,
  choiceHasMore,
  choiceCursors,
  choiceLoading,
  choiceError,
  selections,
  onSelect,
  ready,
  problem,
  periodControls,
  extraControls,
}: {
  model: MarketingPresentation;
  filters: Filters;
  onChange: (changes: Partial<Filters>) => void;
  options: Record<string, Array<string>>;
  totals: Record<string, number>;
  choiceFilters?: LensFilterValues;
  choiceAvailability?: Record<string, Record<string, boolean>>;
  choiceHasMore?: Record<string, boolean>;
  choiceCursors?: Record<string, string>;
  choiceLoading?: boolean;
  choiceError?: boolean;
  selections: Record<string, Array<string>>;
  onSelect: (key: string, values: Array<string>) => void;
  ready: boolean;
  problem?: string;
  periodControls?: React.ReactNode;
  extraControls?: React.ReactNode;
}) {
  const [method, setMethod] = useState(false);
  const [notice, setNotice] = useState('');
  const update = (key: keyof Filters, v: string) =>
    onChange({ [key]: v, ...(key === 'split' ? { highlight: '' } : {}) });
  const attributionModels = [
    ...new Set([...attributeChoices({ options, totals }, 'attribution_model', f.view).values, f.attribution]),
  ];
  const splits = f.view === 'Sales' ? config.splitDimensions.sales : mediaSplits;
  const selected = model.daily;
  const comparison = model.attribution;
  const series = useMemo(() => buildSeries(selected, f), [selected, f]);
  const table = useMemo(
    () => buildTable(f.timeSplit === 'Date Cadence' ? selected : model.summary, f),
    [selected, model.summary, f],
  );
  const visibleSeries = series.series.length > 24 ? series.series.slice(0, 24) : series.series;
  const products = useMemo(() => productRows(model.summary), [model.summary]);
  const channels = useMemo(
    () => Array.from(new Set(comparison.map((r) => label(r.traffic_channel)))).sort(),
    [comparison],
  );
  const models = useMemo(
    () => Array.from(new Set(comparison.map((r) => label(r.attribution_model)))).sort(),
    [comparison],
  );
  const byModelChannel = useMemo(() => {
    const map = new Map<string, Array<Row>>();
    for (const r of comparison) {
      const k = JSON.stringify([label(r.traffic_channel), label(r.attribution_model)]);
      if (!map.has(k)) {
        map.set(k, []);
      }
      map.get(k).push(r);
    }
    return map;
  }, [comparison]);
  const modelTotals = useMemo(
    () => new Map(group(comparison, 'attribution_model', f.metric).map((r) => [r.name, r.rows])),
    [comparison, f.metric],
  );
  const attributionDetail = useMemo(() => {
    const map = new Map<string, Array<Row>>();
    for (const r of comparison) {
      const k = JSON.stringify([label(r.traffic_channel), label(r.attribution_model), label(r.country)]);
      if (!map.has(k)) {
        map.set(k, []);
      }
      map.get(k).push(r);
    }
    return [...map]
      .map(([key, rows]) => ({
        key,
        groups: JSON.parse(key) as Array<string>,
        cost: sum(rows, 'cost'),
        gm: sum(rows, 'gm'),
        roas: value(rows, 'ROAS (Margin) (BR)'),
        ttvRoas: value(rows, 'ROAS (TTV) (BR)'),
      }))
      .sort((a, b) => (b.cost ?? -Infinity) - (a.cost ?? -Infinity));
  }, [comparison]);
  const countries = useMemo(() => Array.from(new Set(comparison.map((r) => label(r.country)))).sort(), [comparison]);
  const attributionLookup = useMemo(() => new Map(attributionDetail.map((r) => [r.key, r])), [attributionDetail]);
  const modelRows = useMemo(
    () => attributionGroupOrder(byModelChannel, f.attribution),
    [byModelChannel, f.attribution],
  );
  const select = (title: string, key: keyof Filters, options: Array<string>) => (
    <SelectControl key={key} title={title} value={f[key]} options={options} onChange={(v) => update(key, v)} />
  );
  const highlight = (name: string) => update('highlight', f.highlight === name ? '' : name);
  const error = problem || validate(f);
  const shownMetrics = f.measures === 'all' ? tableMetrics : coreTableMetrics;
  const activeFilters = Object.entries(selections).filter(([, values]) => values.length);
  const reset = () =>
    onChange({
      ...defaults,
      ...model.defaultDates,
      layout: f.layout,
      theme: f.theme,
      view: f.view,
      filtersOpen: f.filtersOpen,
      measures: f.measures,
      ...(f.view === 'Sales' ? salesViewDefaults : {}),
    });
  function sort(key: string) {
    onChange({ sortBy: key, sort: f.sortBy === key && f.sort === 'desc' ? 'asc' : 'desc' });
  }
  function download() {
    if (!ready) {
      return;
    }
    const csv = marketingCSV(model, f, ready, model.identity);
    const url = URL.createObjectURL(new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = marketingCsvFilename(f);
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setNotice(
      `${csv
        .split('\r\n\r\n')
        .slice(1)
        .reduce(
          (rows, table) => rows + table.split('\r\n').length - 1,
          0,
        )} rows exported for the selected scope. ${f.view === 'Product mix' ? 'Product shares are percentages.' : 'Percentage measures are decimal ratios.'}`,
    );
  }
  const matrix = (title: string, metric: string) => (
    <section className="pm-matrix">
      <h2>{title}</h2>
      <div className="pm-scroll" tabIndex={0} aria-label={title}>
        <table>
          <thead>
            <tr>
              <th>Traffic Channel</th>
              {models.map((m) => (
                <th key={m} title={m}>
                  {m}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="pm-total">
              <th>Grand Total</th>
              {models.map((m) => (
                <td key={m}>{format(value(modelTotals.get(m) ?? [], metric), metric)}</td>
              ))}
            </tr>
            {channels.map((ch) => (
              <tr key={ch}>
                <th>{ch}</th>
                {models.map((m) => (
                  <td key={m} className={m === f.attribution ? 'pm-selected-model' : ''}>
                    {format(value(byModelChannel.get(JSON.stringify([ch, m])) ?? [], metric), metric)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="pm-source">
        {SOURCE} · {f.from} to {f.to} · Models compared separately.
      </p>
    </section>
  );
  return (
    <div className={`pm-report ${f.layout === 'baseline' ? 'pm-conventional' : ''}`} data-theme={f.theme}>
      <header className="pm-header">
        <div>
          <h1>{f.view === 'Attribution' ? 'Comparison across attribution models' : 'Performance Marketing'}</h1>
          <p>
            Data: {f.from} to {f.to} · AUD · Live governed source
          </p>
          <p>
            {[
              f.attribution,
              ...activeFilters.map(
                ([key, values]) =>
                  `${dimensions[key] ?? key}: ${values.slice(0, 5).join(', ')}${values.length > 5 ? ` +${values.length - 5} more` : ''}`,
              ),
            ].join(' · ')}
          </p>
        </div>
        <div className="pm-tools">
          <PageChatButton />
          <button onClick={() => setMethod(true)}>Method and sources</button>
          <button onClick={download} disabled={!ready}>
            Download CSV
          </button>
        </div>
      </header>
      <nav className="pm-view-tabs" aria-label="Workbook views">
        {views.map(([id, title]) => (
          <button
            key={id}
            className={f.view === id ? 'pm-active' : ''}
            aria-pressed={f.view === id}
            onClick={() => update('view', id)}
            title={title}
          >
            {title}
          </button>
        ))}
        <div className="pm-layout">
          <button aria-pressed={f.layout === 'standard'} onClick={() => update('layout', 'standard')}>
            LE
          </button>
          <button aria-pressed={f.layout === 'baseline'} onClick={() => update('layout', 'baseline')}>
            HTML
          </button>
        </div>
      </nav>
      {error && (
        <p role="alert" className="pm-error">
          {error}
        </p>
      )}
      <section className="pm-explore" aria-label="Explore report">
        <div className="pm-primary-controls">
          {periodControls ?? (
            <>
              {f.view !== 'Monthly' && (
                <label className="pm-control">
                  <span>From</span>
                  <input
                    aria-label="From"
                    type="date"
                    value={f.from}
                    onChange={(e) => update('from', e.target.value)}
                  />
                </label>
              )}
              <label className="pm-control">
                <span>To</span>
                <input aria-label="To" type="date" value={f.to} onChange={(e) => update('to', e.target.value)} />
              </label>
            </>
          )}
          {f.view !== 'Monthly' && select('Attribution model', 'attribution', attributionModels)}
          {f.view === 'Performance' && select('Metric', 'metric', originalMetrics)}
          <button
            aria-expanded={f.filtersOpen === 'true'}
            aria-controls="pm-advanced-filters"
            onClick={() => update('filtersOpen', f.filtersOpen === 'true' ? 'false' : 'true')}
          >
            {f.filtersOpen === 'true' ? 'Hide filters' : 'Filters'}
            {activeFilters.length ? ` · ${activeFilters.length} active` : ''}
          </button>
          <button onClick={reset}>Reset</button>
        </div>
        <div className="pm-active-scope" aria-label="Active filter state">
          <span>
            {f.view === 'Performance'
              ? `${dimensions[f.split]} · ${f.cadence} · ${f.timeSplit}`
              : f.view === 'Attribution'
                ? 'Attribution models compared separately'
                : f.view === 'PoP'
                  ? `${dimensions[f.split]} · current period against previous period`
                  : f.view === 'Product mix'
                    ? 'Overlapping product order shares'
                    : ''}
          </span>
          {activeFilters.map(([key, values]) => (
            <button key={key} onClick={() => onSelect(key, [])} aria-label={`Remove ${dimensions[key] ?? key} filter`}>
              {dimensions[key] ?? key}: {values.join(', ')} ×
            </button>
          ))}
        </div>
        {f.filtersOpen === 'true' && (
          <div id="pm-advanced-filters" className="pm-advanced">
            {['Performance', 'PoP', 'Sales'].includes(f.view) && (
              <div>
                <h2>Breakdowns and dates</h2>
                <div className="pm-control-grid">
                  {f.view !== 'PoP' && select('Date Cadence', 'cadence', cadenceOptions)}
                  {select('Data Split 1', 'split', splits)}
                  {f.view !== 'PoP' && select('Data Split 2', 'split2', splits)}
                  {f.view !== 'PoP' && select('Time Split', 'timeSplit', ['Full Period', 'Date Cadence'])}
                </div>
              </div>
            )}
            {viewAttributes[f.view]?.length > 0 && (
              <div>
                <h2>{f.view === 'Sales' ? 'Sales attributes' : 'Marketing attributes'}</h2>
                <div className="pm-attribute-grid">
                  {viewAttributes[f.view].map((key) => {
                    const choice =
                      key === 'transaction_weekday'
                        ? { values: weekdays, total: undefined }
                        : attributeChoices({ options, totals }, key, f.view);
                    const Select = choiceFilters ? CascadingAttributeSelect : AttributeSelect;
                    return (
                      <Select
                        filterKey={key}
                        filters={choiceFilters}
                        availability={choiceAvailability?.[key]}
                        hasMore={choiceHasMore?.[key]}
                        cursor={choiceCursors?.[key]}
                        loading={choiceFilters ? choiceLoading : undefined}
                        searchError={choiceFilters ? choiceError : undefined}
                        key={key}
                        label={dimensions[key] ?? key}
                        options={choice.values}
                        total={choice.total}
                        selected={selections[key] ?? []}
                        onChange={(values) => onSelect(key, values)}
                      />
                    );
                  })}
                </div>
              </div>
            )}
            {extraControls}
          </div>
        )}
      </section>
      <div className="pm-workspace">
        <main className={f.view === 'Attribution' ? 'pm-main pm-attribution' : 'pm-main'}>
          {!ready ? (
            <p role="status" className="pm-empty">
              {problem || 'Waiting for the complete current report scope. Charts, totals and CSV are withheld.'}
            </p>
          ) : ['PoP', 'Sales', 'Monthly'].includes(f.view) ? (
            <SupplementalViews model={model} filters={f} onMeasures={(measures) => update('measures', measures)} />
          ) : f.view === 'Performance' ? (
            <>
              <div className="pm-paired-plots">
                <Trend
                  title="Absolute breakdown"
                  data={series.points}
                  series={visibleSeries}
                  metric={f.metric}
                  theme={f.theme}
                  highlight={f.highlight}
                  onHighlight={highlight}
                  from={f.from}
                  to={f.to}
                  cadence={f.cadence}
                />
                <Trend
                  title="Relative breakdown"
                  data={series.relative}
                  series={visibleSeries}
                  metric={f.metric}
                  theme={f.theme}
                  highlight={f.highlight}
                  onHighlight={highlight}
                  from={f.from}
                  to={f.to}
                  cadence={f.cadence}
                  relative
                />
              </div>
              <div className="pm-inline-legend" aria-label="Chart legend">
                {visibleSeries.map((name, i) => (
                  <button
                    key={name}
                    data-lens-pdf-keep=""
                    aria-pressed={f.highlight === name}
                    className={f.highlight && f.highlight !== name ? 'pm-dimmed' : ''}
                    onClick={() => highlight(name)}
                  >
                    <i style={{ background: f.highlight === name ? '#4EFEB9' : seriesColour(name, i, f.theme) }} />
                    {name}
                  </button>
                ))}
                {f.highlight && <button onClick={() => update('highlight', '')}>Clear highlight</button>}
              </div>
              <section className="pm-tabular">
                <div className="pm-section-heading">
                  <h2>Tabular view</h2>
                  <div className="pm-measures">
                    <button aria-pressed={f.measures !== 'all'} onClick={() => update('measures', 'core')}>
                      Core measures
                    </button>
                    <button aria-pressed={f.measures === 'all'} onClick={() => update('measures', 'all')}>
                      All {tableMetrics.length} measures
                    </button>
                  </div>
                  <span>
                    {table.length} rows · click headers to sort · money in A$ · BR before refunds / AR after refunds
                  </span>
                </div>
                <div className="pm-scroll" tabIndex={0} aria-label="Tabular view">
                  <table>
                    <thead>
                      <tr>
                        <th>Time Split</th>
                        <th>
                          <button onClick={() => sort('Group')}>
                            {dimensions[f.split]} {f.sortBy === 'Group' ? (f.sort === 'desc' ? '↓' : '↑') : ''}
                          </button>
                        </th>
                        <th>{dimensions[f.split2]}</th>
                        {shownMetrics.map((m) => (
                          <th key={m} title={metrics[m].definition}>
                            <button onClick={() => sort(m)}>
                              {m}
                              {f.sortBy === m ? ' ' + (f.sort === 'desc' ? '↓' : '↑') : ''}
                            </button>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="pm-total">
                        <th>Total</th>
                        <th>Selected scope</th>
                        <td></td>
                        {shownMetrics.map((m) => (
                          <td key={m}>{display(value(model.summary, m), m)}</td>
                        ))}
                      </tr>
                      {table.map((r) => (
                        <tr key={r.id} className={f.highlight && r.first !== f.highlight ? 'pm-dimmed' : ''}>
                          <th>{r.time}</th>
                          <th>
                            <button onClick={() => highlight(r.first)} aria-pressed={f.highlight === r.first}>
                              {r.first}
                            </button>
                          </th>
                          <td>{r.second}</td>
                          {shownMetrics.map((m) => (
                            <td key={m}>{display(r.values[m], m)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!table.length && <p className="pm-empty">Unknown · No rows match these filters.</p>}
                </div>
                <p className="pm-source">
                  {SOURCE} · {f.from} to {f.to} · Ratios use aggregate sums, not averages of daily ratios.
                </p>
              </section>
            </>
          ) : f.view === 'Attribution' ? (
            <>
              {matrix('ROAS (GM) (BR)', 'ROAS (Margin) (BR)')}
              {matrix('CR (Orders / Sessions)', 'CR (Orders/Sessions)')}
              <section className="pm-tabular">
                <div className="pm-section-heading">
                  <h2>ROAS (sorted by costs)</h2>
                  <span>Channels ranked by selected model costs · attribution / country</span>
                </div>
                <div className="pm-scroll" tabIndex={0} aria-label="ROAS sorted by costs">
                  <table>
                    <thead>
                      <tr>
                        <th rowSpan={2}>Traffic Channel</th>
                        <th rowSpan={2}>Attribution model</th>
                        {countries.map((c) => (
                          <th colSpan={2} key={c}>
                            {c}
                          </th>
                        ))}
                      </tr>
                      <tr>
                        {countries.flatMap((c) =>
                          ['ROAS (Margin) (BR)', 'ROAS (TTV) (BR)'].map((m) => <th key={c + m}>{m}</th>),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {modelRows.map((r) => (
                        <tr key={r.key}>
                          <th>{r.groups[0]}</th>
                          <th>{r.groups[1]}</th>
                          {countries.flatMap((c) => {
                            const cell = attributionLookup.get(JSON.stringify([...r.groups, c]));
                            return [
                              <td key={c + 'gm'}>{format(cell?.roas ?? null, 'ROAS (Margin) (BR)')}</td>,
                              <td key={c + 'ttv'}>{format(cell?.ttvRoas ?? null, 'ROAS (TTV) (BR)')}</td>,
                            ];
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="pm-source">
                  {SOURCE} · {f.from} to {f.to} · Cost is repeated per attribution model. Never sum models.
                </p>
              </section>
            </>
          ) : (
            <section className="pm-tabular pm-products">
              <h2>Product contribution within attributed orders</h2>
              <p className="pm-note">
                Supplementary source view. Orders can contain multiple products; shares overlap.
              </p>
              <div className="pm-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>GM BR (A$)</th>
                      <th>Attributed orders</th>
                      <th>% of all orders</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p) => (
                      <tr key={p.name}>
                        <th>{p.name}</th>
                        <td>{display(p.gm, 'GM (BR)')}</td>
                        <td>{format(p.orders, 'number')}</td>
                        <td>{format(p.share, 'percent')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="pm-source">
                {SOURCE} · {f.from} to {f.to}. Current source flags; workbook order-share parity unverified.
              </p>
            </section>
          )}
          <p role="status" className="pm-notice">
            {notice ||
              (visibleSeries.length < series.series.length
                ? `${visibleSeries.length} of ${series.series.length} series plotted; all groups remain in the table and CSV.`
                : '')}
          </p>
        </main>
      </div>
      {method && (
        <div className="pm-overlay" role="presentation">
          <section className="pm-dialog" role="dialog" aria-modal="true" aria-label="Method and sources">
            <div className="pm-section-heading">
              <h2>Method and sources</h2>
              <button onClick={() => setMethod(false)}>Close</button>
            </div>
            <p>
              Live governed lux-analytics-prod.analytics.perf_mkt_reporting_roas and analytics.sales_attribution.
              Completed-day boundary {model.asOf} in Australia/Sydney. All displayed totals and exports require the
              matching complete request.
            </p>
            <h3>Original source and fidelity</h3>
            <p>
              The current workbook download, revision 15.4, defines two adjacent absolute/relative stacked-area charts
              above a full-width tabular view, with Dates / Parameters and Marketing Attributes to the right. The
              original default is Daily, GM (BR), Campaign Segment, Data Split 2 All and Full Period. The paired charts
              and table are retained. Controls now sit above the content, with collapsible advanced filters, removable
              active-filter chips and reset. All source-backed filters and 39 table measures remain accessible. Charts
              and tables use natural page scrolling.
            </p>
            <p>
              Original interactive source screenshots remain unavailable. The current revision was downloaded through
              the enabled workbook action; the listed modification is 30 September 2026. Full interactive workflow
              parity and Tableau table-calculation partitions remain unverified. The current workbook formulas and
              visible thumbnail regions have been inspected. Relative breakdown uses within-date percentage share,
              supported by the current normalised stacked-area thumbnail and saved PctTotal / Columns definition.
              Interactive partition parity still needs sign-off.
            </p>
            <h3>Coverage</h3>
            <p>
              Campaign/account/adgroup/placement filters, Sales, PoP and monthly history use the current native source
              definitions. Product mix is supplementary. Original Sales Today/full BoD-MWEB state, Sales All PctTotal
              partition and original PoP discount aliases remain unresolved; full workbook parity is not claimed.
            </p>
            <h3>Attribution and definitions</h3>
            <p>
              Each attribution model repeats media cost. Compare models separately. Router cost, clicks and impressions
              are allocated by session share; they are not independent measurements. Shapley 90D has no sessions,
              sign-ups or router IDs and can carry fractional order credit. All currency is AUD. Website sign-ups are
              not acquired customers; CAC remains Unknown.
            </p>
            {tableMetrics.map((m) => (
              <p key={m}>
                <b>{m}:</b> {metrics[m].definition}
              </p>
            ))}
            <p>
              Historical usage only: 32 viewers / 1,370 events, six months to 25 August 2026. No current ranking is
              asserted.
            </p>
            <a
              href="https://10ay.online.tableau.com/#/site/luxuryescapescomau/views/PerformanceMarketing_17072659984280/ROAS-WIP"
              target="_blank"
              rel="noreferrer"
            >
              Original Tableau
            </a>
            <a
              href="https://github.com/lux-group/lux-analytics/blob/main/models/analytics/perf_mkt_reporting_roas.sql"
              target="_blank"
              rel="noreferrer"
            >
              Warehouse model
            </a>
          </section>
        </div>
      )}
    </div>
  );
}
